import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import routes from '@gik/core/routes';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  return withTitle(
    'Updates and Messages',
    <SWRConfig value={{ fallback }}>
      <LandingPage {...landingPageProps} url="/share-updates" />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'Share Updates',
      content:
        'With Give InKind, you can keep loved ones informed by sharing news, updates, photos, and videos from one centralized place.',
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/hero-hospital-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/hero-hospital-desktop@2x.png`,
    backgroundColor: '#fbf4ed',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      icon: `${getAssetsUrl()}landing-pages/user_updates_gik.png`,
      features: [
        <>
          <span className={'tw-font-bold'}>Keep Everyone Informed, Without the Text Overload:</span> Share important
          news and progress without triggering endless group messages.
        </>,
        <>
          <span className={'tw-font-bold'}>Post Updates Wherever You Choose:</span> Easily share your updates directly
          on your Give InKind page, via email, or across social media.
        </>,
        <>
          <span className={'tw-font-bold'}>Bring Updates to Life with Photos and Videos:</span> Enhance your messages by
          easily adding meaningful visuals to your posts.
        </>,
        <>
          <span className={'tw-font-bold'}>Send Updates Automatically with New Requests:</span> Choose whether to notify
          your community when you add new needs to the calendar.
        </>,
        <>
          <span className={'tw-font-bold'}>Comments and Well-Wishes:</span> Followers can leave comments, well-wishes
          and supportive messages on Updates.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'Real Stories: Shannon',
        content:
          'Read more about how Shannon used Give InKind to keep her community updated through her cancer treatment, sharing video blogs as her friends and family cheered her on.',
        icon: `${getAssetsUrl()}landing-pages/features/Updates-Feature01.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/breast-cancer-victory-team-shannons-story',
      },
      {
        title: 'How is Give InKind different?',
        content:
          'Learn how Give InKind compares to other websites like CaringBridge for sharing Updates along the way.',
        icon: `${getAssetsUrl()}landing-pages/features/Updates-Feature02.png`,
        buttonTitle: 'Read More',
        buttonLink: routes.competitors,
      },
      {
        title: 'Turning Organized Support into a Superpower',
        content: `When crisis strikes, everyone wants to know "how can I help?". When you take the first step to organizing that help, you can be everyone's hero. We'll help you get started.`,
        icon: `${getAssetsUrl()}landing-pages/features/Updates-Feature03.png`,
        buttonTitle: 'Read More',
        buttonLink: '/articles/turning-support-superpower-give-inkind-advantage',
      },
    ],
  },
  blogSection: undefined,
};
